import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, Inbox, Package, School as SchoolIcon, Store, ShoppingBag, Wallet, Plus, Search,
  Edit3, ToggleLeft, ToggleRight, RefreshCw, X, KeyRound, CheckCircle2,
  AlertTriangle, Copy, Users, Percent, Star, Trash2, BarChart3,
  Settings as SettingsIcon,
} from "lucide-react";

const NAV = [
  { to: "/super/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/super/schools", label: "Schools", icon: SchoolIcon },
  { to: "/super/products", label: "Products", icon: Package },
  { to: "/super/vendors", label: "Vendors", icon: Store },
  { to: "/super/orders", label: "Orders & Ops", icon: ShoppingBag },
  { to: "/super/finance", label: "Finance", icon: Wallet },
  { to: "/super/enquiries", label: "Enquiries", icon: Inbox },
  { to: "/super/settings", label: "Settings", icon: SettingsIcon },
];

const EMPTY_VENDOR = {
  name: "", company_name: "", email: "", phone: "",
  gstin: "", pan: "",
  categories: [], payment_terms_days: 30,
  address_line1: "", address_line2: "", city: "", state: "", pincode: "",
  contact_persons: [], notes: "", active: true,
};

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function Vendors() {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [createdCred, setCreatedCred] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (catFilter) params.category = catFilter;
      if (activeFilter) params.active = activeFilter;
      const [{ data }, catRes] = await Promise.all([
        api.get("/admin/vendors", { params }),
        categories.length ? Promise.resolve({ data: categories }) : api.get("/categories"),
      ]);
      setRows(data);
      if (!categories.length) setCategories(catRes.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const stats = useMemo(() => {
    const total = rows.length;
    const active = rows.filter((r) => r.active).length;
    const catCount = new Set(rows.flatMap((r) => r.categories || [])).size;
    return { total, active, inactive: total - active, catCount };
  }, [rows]);

  const toggleActive = async (r) => {
    try {
      if (r.active) {
        await api.delete(`/admin/vendors/${r.id}`);
        setRows((s) => s.map((x) => (x.id === r.id ? { ...x, active: false } : x)));
        toast.success(`${r.company_name} deactivated`);
      } else {
        const { data } = await api.patch(`/admin/vendors/${r.id}`, { active: true });
        setRows((s) => s.map((x) => (x.id === r.id ? { ...x, ...data } : x)));
        toast.success(`${r.company_name} reactivated`);
      }
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const save = async (form) => {
    try {
      if (form.id) {
        const { data } = await api.patch(`/admin/vendors/${form.id}`, {
          name: form.name, company_name: form.company_name,
          phone: form.phone, gstin: form.gstin, pan: form.pan,
          categories: form.categories, payment_terms_days: Number(form.payment_terms_days),
          address_line1: form.address_line1, address_line2: form.address_line2,
          city: form.city, state: form.state, pincode: form.pincode,
          contact_persons: (form.contact_persons || []).filter((c) => c.name?.trim()),
          notes: form.notes, active: !!form.active,
        });
        setRows((s) => s.map((x) => (x.id === form.id ? { ...x, ...data } : x)));
        toast.success("Vendor updated");
      } else {
        const { data } = await api.post("/admin/vendors", {
          name: form.name, company_name: form.company_name,
          email: form.email, phone: form.phone,
          gstin: form.gstin, pan: form.pan,
          categories: form.categories, payment_terms_days: Number(form.payment_terms_days),
          address_line1: form.address_line1, address_line2: form.address_line2,
          city: form.city, state: form.state, pincode: form.pincode,
          contact_persons: (form.contact_persons || []).filter((c) => c.name?.trim()),
          notes: form.notes, active: !!form.active,
        });
        setRows((s) => [{ ...data.vendor, products_count: 0, purchase_orders_count: 0 }, ...s]);
        setCreatedCred({ email: data.vendor.email, password: data.initial_password });
        toast.success("Vendor added — copy the initial password now");
      }
      setEditing(null);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <DashboardLayout nav={NAV} title="Vendor Management">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <div className="text-2xl font-heading font-semibold text-brand-navy" data-testid="vendors-title">
            Vendors
          </div>
          <div className="text-sm text-slate-500">
            {stats.total} total · {stats.active} active · {stats.catCount} {stats.catCount === 1 ? "category" : "categories"} covered
          </div>
        </div>
        <button
          onClick={() => setEditing({ ...EMPTY_VENDOR })}
          data-testid="vendors-add-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blueDark"
        >
          <Plus size={14} /> Add Vendor
        </button>
      </div>

      {createdCred && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm mb-4 flex items-start gap-3" data-testid="vendors-cred-banner">
          <AlertTriangle size={16} className="text-amber-700 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-amber-800">Copy this vendor's initial password now — it won't be shown again.</div>
            <div className="mt-2 flex items-center gap-2">
              <code className="flex-1 bg-white border border-amber-200 rounded px-3 py-2 font-mono">{createdCred.password}</code>
              <button onClick={() => { navigator.clipboard.writeText(createdCred.password); toast.success("Copied"); }}
                className="p-2 rounded hover:bg-white/60"><Copy size={14} /></button>
              <button onClick={() => setCreatedCred(null)}
                className="text-amber-800 text-xs">Dismiss</button>
            </div>
            <div className="mt-1 text-amber-700 text-xs">For: {createdCred.email}</div>
          </div>
        </div>
      )}

      <div className="sl-card p-4 mb-5 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="vendors-search"
            placeholder="Search company, email, supplier ref…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="pl-9 pr-4 py-2 w-full rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-brand-blue"
          />
        </div>
        <select data-testid="vendors-category-filter"
          value={catFilter} onChange={(e) => setCatFilter(e.target.value)}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm bg-white">
          <option value="">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="vendors-active-filter"
          value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm bg-white">
          <option value="">Any status</option>
          <option value="true">Active only</option>
          <option value="false">Inactive only</option>
        </select>
        <button onClick={load} data-testid="vendors-apply-filters"
          className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-700">Apply</button>
        <button onClick={() => { setQ(""); setCatFilter(""); setActiveFilter(""); setTimeout(load, 0); }}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-slate-300 text-sm hover:border-slate-400">
          <RefreshCw size={13} /> Clear
        </button>
      </div>

      <div className="sl-card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3">Vendor</th>
              <th className="text-left px-4 py-3">GSTIN</th>
              <th className="text-left px-4 py-3">Categories</th>
              <th className="text-center px-4 py-3">Terms</th>
              <th className="text-right px-4 py-3">Products</th>
              <th className="text-right px-4 py-3">POs</th>
              <th className="text-center px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="text-center py-10 text-slate-500">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="text-center py-12 text-slate-500" data-testid="vendors-empty">
                No vendors match your filters.
              </td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer"
                  data-testid={`vendor-row-${r.id}`}
                  onClick={() => setDetailId(r.id)}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-800">{r.company_name || r.name}</div>
                  <div className="text-xs text-slate-500">{r.email} · {r.supplier_ref}</div>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600 font-mono">{r.gstin || "—"}</td>
                <td className="px-4 py-3 text-xs text-slate-600">
                  {r.categories?.length ? (
                    <div className="flex flex-wrap gap-1">
                      {r.categories.slice(0, 2).map((c) => (
                        <span key={c} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{c}</span>
                      ))}
                      {r.categories.length > 2 && <span className="text-slate-500">+{r.categories.length - 2}</span>}
                    </div>
                  ) : "—"}
                </td>
                <td className="px-4 py-3 text-center text-slate-700">NET-{r.payment_terms_days}</td>
                <td className="px-4 py-3 text-right text-slate-700">{r.products_count}</td>
                <td className="px-4 py-3 text-right text-slate-700">{r.purchase_orders_count}</td>
                <td className="px-4 py-3 text-center">
                  {r.active ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-brand-greenSoft text-brand-greenDark text-xs font-semibold">
                      <CheckCircle2 size={12} /> Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">Inactive</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="inline-flex items-center gap-1">
                    <button onClick={() => setEditing({ ...r, payment_terms_days: String(r.payment_terms_days) })}
                      data-testid={`vendor-edit-${r.id}`}
                      className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="Edit"><Edit3 size={15} /></button>
                    <button onClick={() => toggleActive(r)}
                      data-testid={`vendor-toggle-${r.id}`}
                      className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                      title={r.active ? "Deactivate" : "Reactivate"}>
                      {r.active ? <ToggleRight size={17} className="text-brand-green" /> : <ToggleLeft size={17} className="text-slate-400" />}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <VendorEditor
          initial={editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
      {detailId && (
        <VendorDetail
          id={detailId}
          categories={categories}
          onClose={() => setDetailId(null)}
          onEdit={(v) => { setDetailId(null); setEditing({ ...v, payment_terms_days: String(v.payment_terms_days) }); }}
          onRefresh={load}
        />
      )}
    </DashboardLayout>
  );
}

/* ------------- Editor ------------- */
function VendorEditor({ initial, categories, onClose, onSave }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(f.id);
  const set = (k) => (e) => setF((s) => ({
    ...s,
    [k]: e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e,
  }));

  const toggleCat = (c) => setF((s) => ({
    ...s,
    categories: s.categories.includes(c)
      ? s.categories.filter((x) => x !== c)
      : [...s.categories, c],
  }));

  const setContact = (idx, k, v) => setF((s) => ({
    ...s,
    contact_persons: s.contact_persons.map((c, i) => (i === idx ? { ...c, [k]: v } : c)),
  }));
  const addContact = () => setF((s) => ({ ...s, contact_persons: [...(s.contact_persons || []), { name: "", role: "", email: "", phone: "" }] }));
  const rmContact = (idx) => setF((s) => ({ ...s, contact_persons: s.contact_persons.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.company_name.trim().length < 2) return toast.error("Company name is required");
    if (!isEdit && !/^\S+@\S+\.\S+$/.test(f.email)) return toast.error("Valid email required");
    setSaving(true);
    await onSave(f);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="vendor-editor">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <form onSubmit={submit} className="w-full max-w-2xl bg-white border-l border-slate-200 shadow-xl overflow-y-auto">
        <div className="p-6 border-b border-slate-100 flex items-start justify-between gap-4 sticky top-0 bg-white z-10">
          <div>
            <div className="text-xs uppercase tracking-widest text-brand-greenDark font-semibold">
              {isEdit ? "Edit vendor" : "Add vendor"}
            </div>
            <h3 className="mt-1 text-lg font-heading font-semibold text-brand-navy">
              {isEdit ? (f.company_name || f.name) : "New vendor onboarding"}
            </h3>
          </div>
          <button type="button" onClick={onClose} data-testid="vendor-editor-close"
                  className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-5 text-sm">
          <section>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Company &amp; login</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-slate-600">Company name *</label>
                <input required data-testid="vendor-company"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
                  value={f.company_name} onChange={set("company_name")} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Primary contact name *</label>
                <input required data-testid="vendor-name"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
                  value={f.name} onChange={set("name")} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Phone</label>
                <input data-testid="vendor-phone"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
                  value={f.phone} onChange={set("phone")} />
              </div>
              {!isEdit && (
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-slate-600">Login email *</label>
                  <input required type="email" data-testid="vendor-email"
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
                    value={f.email} onChange={set("email")}
                    placeholder="finance@vendor.co.in" />
                  <div className="text-xs text-slate-500 mt-1">
                    The vendor will receive a one-time password after creation.
                  </div>
                </div>
              )}
            </div>
          </section>

          <section>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Tax &amp; terms</div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-slate-600">GSTIN</label>
                <input data-testid="vendor-gstin"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 uppercase font-mono"
                  value={f.gstin} onChange={set("gstin")} maxLength={15} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Payment terms (days)</label>
                <input type="number" min="0" max="180" data-testid="vendor-terms"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
                  value={f.payment_terms_days} onChange={set("payment_terms_days")} />
              </div>
              <div className="sm:col-span-3">
                <label className="text-xs font-semibold text-slate-600">PAN (optional)</label>
                <input className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 uppercase font-mono"
                  value={f.pan} onChange={set("pan")} maxLength={10} />
              </div>
            </div>
          </section>

          <section>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Eligible categories</div>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => {
                const on = f.categories.includes(c);
                return (
                  <button type="button" key={c}
                    onClick={() => toggleCat(c)}
                    data-testid={`vendor-cat-${c.toLowerCase().replace(/\s+/g,'-').replace(/&/g,'')}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      on ? "bg-brand-blue text-white border-brand-blue" : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                    }`}>
                    {c}
                  </button>
                );
              })}
            </div>
          </section>

          <section>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Address</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input placeholder="Address line 1" value={f.address_line1} onChange={set("address_line1")}
                className="sm:col-span-2 rounded-md border border-slate-300 px-3 py-2" />
              <input placeholder="Address line 2" value={f.address_line2} onChange={set("address_line2")}
                className="sm:col-span-2 rounded-md border border-slate-300 px-3 py-2" />
              <input placeholder="City" value={f.city} onChange={set("city")}
                className="rounded-md border border-slate-300 px-3 py-2" />
              <input placeholder="State" value={f.state} onChange={set("state")}
                className="rounded-md border border-slate-300 px-3 py-2" />
              <input placeholder="Pincode" value={f.pincode} onChange={set("pincode")}
                className="rounded-md border border-slate-300 px-3 py-2" />
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Contact persons</div>
              <button type="button" onClick={addContact} data-testid="vendor-add-contact"
                className="text-xs font-semibold text-brand-blue hover:text-brand-blueDark inline-flex items-center gap-1">
                <Plus size={12} /> Add contact
              </button>
            </div>
            <div className="space-y-2">
              {(f.contact_persons || []).map((c, idx) => (
                <div key={idx} className="grid sm:grid-cols-4 gap-2 rounded-lg border border-slate-200 p-2">
                  <input placeholder="Name" value={c.name} onChange={(e) => setContact(idx, "name", e.target.value)}
                    className="rounded border border-slate-300 px-2 py-1.5 text-sm" />
                  <input placeholder="Role" value={c.role} onChange={(e) => setContact(idx, "role", e.target.value)}
                    className="rounded border border-slate-300 px-2 py-1.5 text-sm" />
                  <input placeholder="Email" value={c.email} onChange={(e) => setContact(idx, "email", e.target.value)}
                    className="rounded border border-slate-300 px-2 py-1.5 text-sm" />
                  <div className="flex items-center gap-1">
                    <input placeholder="Phone" value={c.phone} onChange={(e) => setContact(idx, "phone", e.target.value)}
                      className="flex-1 rounded border border-slate-300 px-2 py-1.5 text-sm" />
                    <button type="button" onClick={() => rmContact(idx)}
                      className="p-1.5 rounded hover:bg-red-50 text-red-500"><X size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <label className="text-xs font-semibold text-slate-600">Internal notes</label>
            <textarea rows={2} value={f.notes} onChange={set("notes")}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
          </section>
        </div>

        <div className="p-6 border-t border-slate-100 flex items-center gap-3">
          <button type="submit" disabled={saving} data-testid="vendor-save"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-brand-blue text-white font-semibold hover:bg-brand-blueDark disabled:opacity-60">
            {saving ? "Saving…" : (isEdit ? "Save changes" : "Add vendor")}
          </button>
          <button type="button" onClick={onClose}
            className="px-5 py-2.5 rounded-md border border-slate-300 hover:border-slate-400">Cancel</button>
        </div>
      </form>
    </div>
  );
}

/* ------------- Detail drawer ------------- */
function VendorDetail({ id, categories, onClose, onEdit, onRefresh }) {
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [pDraft, setPDraft] = useState({ product_id: "", cost_price: "", lead_time_days: "7", moq: "1", preferred: false });
  const [resetPw, setResetPw] = useState(null);

  const load = async () => {
    try {
      const [{ data: d }, { data: p }] = await Promise.all([
        api.get(`/admin/vendors/${id}`),
        api.get("/admin/products?limit=1000"),
      ]);
      setData(d);
      setProducts(p);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const addMapping = async () => {
    if (!pDraft.product_id || !pDraft.cost_price) {
      return toast.error("Product and cost price are required");
    }
    try {
      await api.post("/admin/product-vendors", {
        product_id: pDraft.product_id,
        vendor_id: id,
        cost_price: Number(pDraft.cost_price),
        lead_time_days: parseInt(pDraft.lead_time_days || 7, 10),
        moq: parseInt(pDraft.moq || 1, 10),
        preferred: !!pDraft.preferred,
      });
      toast.success("Product linked");
      setPDraft({ product_id: "", cost_price: "", lead_time_days: "7", moq: "1", preferred: false });
      setAddOpen(false);
      load();
      onRefresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeMapping = async (pvid) => {
    try {
      await api.delete(`/admin/product-vendors/${pvid}`);
      toast.success("Product unlinked");
      load();
      onRefresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const togglePreferred = async (m) => {
    try {
      await api.patch(`/admin/product-vendors/${m.id}`, { preferred: !m.preferred });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const resetPassword = async () => {
    try {
      const { data: r } = await api.post(`/admin/vendors/${id}/reset-password`, {});
      setResetPw(r);
      toast.success("Temporary password generated");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const availableProducts = useMemo(() => {
    const linked = new Set((data?.product_mappings || []).map((m) => m.product_id));
    return products.filter((p) => !linked.has(p.id));
  }, [products, data]);

  if (!data) return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="vendor-detail">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-2xl bg-white border-l border-slate-200 shadow-xl p-8 text-center text-slate-500">Loading…</div>
    </div>
  );

  const v = data.vendor;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="vendor-detail">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-3xl bg-white border-l border-slate-200 shadow-xl overflow-y-auto">
        <div className="p-6 border-b border-slate-100 flex items-start justify-between gap-4 sticky top-0 bg-white z-10">
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-widest text-brand-greenDark font-semibold">Vendor profile</div>
            <h3 className="mt-1 text-xl font-heading font-semibold text-brand-navy truncate">
              {v.company_name}
            </h3>
            <div className="mt-1 text-xs text-slate-500">
              {v.email} · {v.supplier_ref} · NET-{v.payment_terms_days} terms
              {v.gstin && <> · GSTIN {v.gstin}</>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => onEdit(v)} data-testid="vendor-detail-edit"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-300 hover:border-slate-400 text-sm">
              <Edit3 size={13} /> Edit
            </button>
            <button onClick={resetPassword} data-testid="vendor-detail-reset"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-300 hover:border-slate-400 text-sm">
              <KeyRound size={13} /> Reset password
            </button>
            <button onClick={onClose} data-testid="vendor-detail-close"
              className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
          </div>
        </div>

        <div className="p-6 space-y-6 text-sm">
          {/* Stats */}
          <div className="grid grid-cols-4 gap-3">
            <StatCard icon={Package} label="Products" value={v.products_count} />
            <StatCard icon={ShoppingBag} label="Purchase Orders" value={v.purchase_orders_count} />
            <StatCard icon={Users} label="Contacts" value={(v.contact_persons || []).length} />
            <StatCard icon={CheckCircle2} label="Categories" value={(v.categories || []).length} />
          </div>

          {resetPw && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs" data-testid="vendor-reset-result">
              <div className="flex items-center gap-2 text-amber-800 font-semibold">
                <AlertTriangle size={13} /> Copy this password now — it won't be shown again.
              </div>
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 bg-white border border-amber-200 rounded px-3 py-2 font-mono text-sm text-slate-800">{resetPw.new_password}</code>
                <button onClick={() => { navigator.clipboard.writeText(resetPw.new_password); toast.success("Copied"); }}
                  className="p-2 rounded hover:bg-white/60"><Copy size={13} /></button>
                <button onClick={() => setResetPw(null)} className="text-amber-800 text-xs">Dismiss</button>
              </div>
            </div>
          )}

          {/* Product mappings */}
          <section className="rounded-xl border border-slate-200 bg-white">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Percent size={16} className="text-brand-blue" />
                <div className="font-heading font-semibold text-brand-navy">Product mappings &amp; margin</div>
                <span className="text-xs text-slate-500">({data.product_mappings.length})</span>
              </div>
              <button onClick={() => setAddOpen((s) => !s)} data-testid="vendor-add-mapping"
                className="text-xs font-semibold text-brand-blue hover:text-brand-blueDark inline-flex items-center gap-1">
                <Plus size={12} /> Link product
              </button>
            </div>

            {addOpen && (
              <div className="p-4 border-b border-slate-100 bg-slate-50" data-testid="vendor-mapping-form">
                <div className="grid sm:grid-cols-6 gap-2">
                  <select data-testid="vendor-mapping-product"
                    value={pDraft.product_id}
                    onChange={(e) => setPDraft((s) => ({ ...s, product_id: e.target.value }))}
                    className="sm:col-span-3 rounded-md border border-slate-300 px-3 py-2 bg-white text-sm">
                    <option value="">Choose product…</option>
                    {availableProducts.map((p) => (
                      <option key={p.id} value={p.id}>{p.name} · MRP ₹{p.price}</option>
                    ))}
                  </select>
                  <input placeholder="Cost ₹" type="number" min="0" step="0.01"
                    data-testid="vendor-mapping-cost"
                    value={pDraft.cost_price}
                    onChange={(e) => setPDraft((s) => ({ ...s, cost_price: e.target.value }))}
                    className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
                  <input placeholder="Lead days" type="number" min="0"
                    value={pDraft.lead_time_days}
                    onChange={(e) => setPDraft((s) => ({ ...s, lead_time_days: e.target.value }))}
                    className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
                  <input placeholder="MOQ" type="number" min="1"
                    value={pDraft.moq}
                    onChange={(e) => setPDraft((s) => ({ ...s, moq: e.target.value }))}
                    className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <label className="text-xs flex items-center gap-1 text-slate-700">
                    <input type="checkbox" checked={pDraft.preferred}
                      onChange={(e) => setPDraft((s) => ({ ...s, preferred: e.target.checked }))} />
                    Mark as preferred supplier
                  </label>
                  <button onClick={addMapping} data-testid="vendor-mapping-save"
                    className="px-4 py-1.5 rounded-md bg-brand-blue text-white text-xs font-semibold hover:bg-brand-blueDark">
                    Link
                  </button>
                </div>
              </div>
            )}

            {data.product_mappings.length === 0 ? (
              <div className="p-6 text-center text-xs text-slate-500">
                No products linked yet — this vendor has no catalog under SchoolLane.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="text-left px-4 py-2">Product</th>
                      <th className="text-left px-4 py-2">Category</th>
                      <th className="text-right px-4 py-2">MRP</th>
                      <th className="text-right px-4 py-2">Cost</th>
                      <th className="text-right px-4 py-2">Margin</th>
                      <th className="text-center px-4 py-2">Lead</th>
                      <th className="text-center px-4 py-2">MOQ</th>
                      <th className="text-center px-4 py-2">Preferred</th>
                      <th className="text-right px-4 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.product_mappings.map((m) => (
                      <tr key={m.id} className="border-t border-slate-100"
                          data-testid={`vendor-mapping-row-${m.id}`}>
                        <td className="px-4 py-2 font-semibold text-slate-800">{m.product_name}</td>
                        <td className="px-4 py-2 text-slate-600">{m.product_category}</td>
                        <td className="px-4 py-2 text-right">{fmtINR(m.mrp)}</td>
                        <td className="px-4 py-2 text-right text-slate-700">{fmtINR(m.cost_price)}</td>
                        <td className="px-4 py-2 text-right">
                          <span className={`font-semibold ${m.margin_pct >= 20 ? "text-brand-greenDark" : m.margin_pct >= 10 ? "text-amber-600" : "text-red-600"}`}>
                            {m.margin_pct}%
                          </span>
                          <div className="text-[10px] text-slate-500">{fmtINR(m.margin_amount)}</div>
                        </td>
                        <td className="px-4 py-2 text-center text-slate-600">{m.lead_time_days}d</td>
                        <td className="px-4 py-2 text-center text-slate-600">{m.moq}</td>
                        <td className="px-4 py-2 text-center">
                          <button onClick={() => togglePreferred(m)}
                            data-testid={`vendor-mapping-preferred-${m.id}`}
                            title={m.preferred ? "Preferred" : "Mark preferred"}>
                            <Star size={14} className={m.preferred ? "text-amber-500 fill-amber-500" : "text-slate-300"} />
                          </button>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <button onClick={() => removeMapping(m.id)}
                            data-testid={`vendor-mapping-remove-${m.id}`}
                            className="p-1 rounded hover:bg-red-50 text-red-500"><Trash2 size={13} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Contacts */}
          {v.contact_persons?.length > 0 && (
            <section>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Contact persons</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {v.contact_persons.map((c, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 bg-white p-3 text-xs">
                    <div className="font-semibold text-slate-800">{c.name}</div>
                    {c.role && <div className="text-slate-500">{c.role}</div>}
                    {c.email && <div className="mt-1 text-slate-600">{c.email}</div>}
                    {c.phone && <div className="text-slate-600">{c.phone}</div>}
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex items-center gap-1.5 text-xs text-slate-500 uppercase tracking-wide">
        <Icon size={12} /> {label}
      </div>
      <div className="mt-1 text-lg font-heading font-bold text-brand-navy">{value}</div>
    </div>
  );
}
